import axios from 'axios';
import { pick } from 'lodash';
import { createActionHelpers } from 'vuex-loading';
import * as types from '../mutation-types';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const mapToServerObject = movement => {
    let group_details = [];

    group_details = movement.group_details
        .map(g => {
            let tank_details = [];

            for (const dipping of movement.dipping_details) {
                if (dipping.group_no !== g.group_no) {
                    continue;
                }

                // Combine movement date with dipping timestamp
                const movementDayMoment = moment(movement.movement_day);

                dipping.dipping_date = moment(dipping.dipping_date)
                    .year(movementDayMoment.year())
                    .month(movementDayMoment.month())
                    .date(movementDayMoment.date())
                    .format();

                const dippingDetails = _.pick(dipping, [
                    'id',
                    'tank_id',
                    'tank_code',
                    'balance',
                    'dipping_date',
                    'user_id',
                    'system_volume',
                    'variance',
                ]);
                dippingDetails.system_volume = numeral(
                    dippingDetails.system_volume,
                ).value();

                tank_details.push(dippingDetails);
            }

            // if tank details empty, skip
            if (tank_details.length == 0) {
                return null;
            }

            g.tank_details = tank_details;

            return createGroupDetails(g, true);
        })
        .filter(g => {
            return g !== null;
        });

    return {
        ...pick(movement, ['movement_day', 'timezone']),
        group_details,
    };
};

const createGroupDetails = (input, forServer = false) => {
    let properties = [
        'id',
        'tank_details',
        'fuel_id',
        'total_balance',
        'variance',
        'group_no',
        'row_index',
        'fuel_grade',
        'tank_group_id',
    ];

    if (forServer) {
        properties = properties.filter(
            p => !['group_no', 'row_index', 'fuel_grade'].includes(p),
        );
    }

    return pick(input, properties);
};

const createDippingDetails = (input, forServer = false) => {
    let properties = [
        'id',
        'group_no',
        'tank_id',
        'tank_code',
        'fuel_grade',
        'balance',
        'capacity',
        'dipping_date',
        'user_id',
        'system_volume',
    ];

    if (forServer) {
        properties = properties.filter(
            p => !['group_no', 'fuel_grade'].includes(p),
        );
    }

    return pick(input, properties);
};

const state = {
    movement: {
        id: null,
        status: null,
        movement_day: null,
        timezone: null,
        dipping_details: [],
        deleted_at: null,
        user_id: null,
        group_details: [],
    },
};

const getters = {
    movement: state => state.movement,

    dippingDetails: state => state.movement.dipping_details,

    movementDay: state => state.movement.movement_day,

    findDippingDetailIndex: (state, getters) => tank_id => {
        return getters.dippingDetails.findIndex(d => d.tank_id === tank_id);
    },

    findDippingsByGroupNo: (state, getters) => group_no => {
        return getters.dippingDetails.filter(r => r.group_no === group_no);
    },

    groupDetails: state => state.movement.group_details,

    findGroupDetailsIndex: (state, getters) => group_no => {
        return getters.groupDetails.findIndex(g => g.group_no === group_no);
    },

    findGroupByFuelId: (state, getters) => fuel_id => {
        return getters.groupDetails.find(g => g.fuel_id === fuel_id);
    },

    findGroupByGroupNo: (state, getters) => group_no => {
        return getters.groupDetails.find(g => g.group_no === group_no);
    },

    findGroupByTankGroupId: (state, getters) => tank_group_id => {
        return getters.groupDetails.find(
            g => g.tank_group_id === tank_group_id,
        );
    },
};

const mutations = {
    [types.UPDATE_FUEL_DIPPING_MOVEMENT](state, movement) {
        state.movement = { ...state.movement, ...movement };
    },

    [types.UPDATE_FUEL_DIPPING_DETAIL](
        state,
        { dippingDetailIndex, attr, value },
    ) {
        state.movement.dipping_details[dippingDetailIndex][attr] = value;
    },

    [types.RESET_FUEL_DIPPING_DETAILS](state) {
        state.movement.dipping_details = [];
    },

    [types.ADD_FUEL_DIPPING_DETAIL](state, details) {
        state.movement.dipping_details.push(details);
    },

    [types.INITIALIZE_GROUP_DETAILS](state, groupDetails) {
        state.movement.group_details = groupDetails;
    },

    [types.UPDATE_GROUP_DETAILS](state, { groupDetailIndex, attr, value }) {
        state.movement.group_details[groupDetailIndex][attr] = value;
    },

    [types.RESET_GROUP_DETAILS](state) {
        state.movement.group_details = [];
    },
};

const actions = {
    updateMovement({ commit }, movement) {
        commit(types.UPDATE_FUEL_DIPPING_MOVEMENT, movement);
    },

    updateDippingDetails({ commit, getters }, { tank_id, attr, value }) {
        const dippingDetailIndex = getters.findDippingDetailIndex(tank_id);

        commit(types.UPDATE_FUEL_DIPPING_DETAIL, {
            dippingDetailIndex,
            attr,
            value,
        });
    },

    resetDippingDetails({ commit }) {
        commit(types.RESET_FUEL_DIPPING_DETAILS);
    },

    addDippingDetail({ commit, getters, dispatch }, detail) {
        const group = getters.findGroupByTankGroupId(detail.tank_group_id);

        if (!group.fuel_grade) {
            dispatch('updateGroupDetails', {
                fuel_id: detail.fuel_id,
                attr: 'fuel_grade',
                value: detail.fuel_grade,
            });
        }

        detail.group_no = group.group_no;

        const dippingDetail = createDippingDetails(detail);
        commit(types.ADD_FUEL_DIPPING_DETAIL, dippingDetail);
    },

    initializeDippingGroups({ commit }, tankList) {
        const fuelGroups = [];
        let ctr = 1;

        for (const tank of tankList) {
            if (
                fuelGroups.find(
                    group => group.tank_group_id === tank.tank_group_id,
                )
            ) {
                continue;
            }

            const group = {
                ...tank,
                fuel_grade: tank.fuel_name,
                tank_group_id: tank.tank_group_id,
                row_index: null,
                group_no: ctr++,
                total_balance: null,
                variance: 0,
            };

            fuelGroups.push(createGroupDetails(group));
        }

        commit(types.INITIALIZE_GROUP_DETAILS, fuelGroups);
    },

    setExistingDippingGroups({ commit, getters }, dippings) {
        const fuelGroups = [];
        let ctr = 1;

        for (const dipping of dippings) {
            if (
                fuelGroups.find(
                    group => group.tank_group_id === dipping.tank.tank_group_id,
                )
            ) {
                continue;
            }

            let totalBalance = 0;
            let variance = 0;

            if (!!dipping.dipping_group) {
                totalBalance = dipping.dipping_group.total_balance;
                variance = dipping.dipping_group.variance;
            }

            const group = {
                id: dipping.dipping_group.id,
                fuel_id: dipping.tank.fuel_id,
                fuel_grade: dipping.tank.fuel_name,
                tank_group_id: dipping.tank.tank_group_id,
                row_index: null,
                group_no: ctr++,
                total_balance: totalBalance,
                variance: variance,
            };

            fuelGroups.push(createGroupDetails(group));
        }

        commit(types.INITIALIZE_GROUP_DETAILS, fuelGroups);
    },

    updateGroupDetails({ commit, getters }, { group_no, attr, value }) {
        const groupDetailIndex = getters.findGroupDetailsIndex(group_no);

        commit(types.UPDATE_GROUP_DETAILS, {
            groupDetailIndex,
            attr,
            value,
        });
    },

    resetGroupDetails({ commit }) {
        commit(types.RESET_GROUP_DETAILS);
    },

    calculateAndSaveTotalBalanceAndVariance({ getters, dispatch }, group) {
        const dippings = getters.findDippingsByGroupNo(group.group_no);

        let totalBalance = 0;
        let totalSystemVolume = 0;

        for (const cur of dippings) {
            totalBalance = totalBalance + numeral(cur.balance).value();
            totalSystemVolume =
                totalSystemVolume + numeral(cur.system_volume).value();
        }

        dispatch('updateGroupDetails', {
            group_no: group.group_no,
            attr: 'total_balance',
            value: totalBalance,
        });

        const variance = totalBalance - totalSystemVolume;
        dispatch('updateGroupDetails', {
            group_no: group.group_no,
            attr: 'variance',
            value: variance,
        });
    },

    async createDippingMovement({ dispatch, state }) {
        const url = Laroute.route(
            'franchise.inventory.fuel.dipping.create-movement',
        );
        startLoading(dispatch, 'save-dipping');

        try {
            const { data } = await axios.post(
                url,
                mapToServerObject(state.movement),
            );
            endLoading(dispatch, 'save-dipping');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-dipping');
            throw e;
        }
    },

    async updateDippingMovement({ dispatch, state }) {
        const url = Laroute.route(
            'franchise.inventory.fuel.dipping.update-movement',
            { id: state.movement.id },
        );
        startLoading(dispatch, 'save-dipping');

        try {
            const { data } = await axios.patch(
                url,
                mapToServerObject(state.movement),
            );
            endLoading(dispatch, 'save-dipping');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-dipping');
            throw e;
        }
    },

    async commitDippingMovement({ dispatch, state }) {
        const url = Laroute.route(
            'franchise.inventory.fuel.dipping.commit-movement',
            { id: state.movement.id },
        );
        startLoading(dispatch, 'commit-dipping');

        try {
            const { data } = await axios.post(url);
            endLoading(dispatch, 'commit-dipping');

            return data;
        } catch (e) {
            endLoading(dispatch, 'commit-dipping');
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/fuel-dipping.js